import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../src/theme';
import { Header } from '@components/layout';
import { Button, ErrorBanner, Input } from '@components/common';
import { useAuthStore } from '../../src/store/authStore';
import { getErrorMessage } from '../../src/utils/errorHandler';

export default function InviteCodeScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, redeemInviteCode } = useAuthStore();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!code.trim() || !user) return;
    setIsValidating(true);
    setError(null);

    try {
      // invitedByAgentId/defaultInsurer are create-frozen by rules; the
      // callable validates the code and writes the linkage via admin SDK.
      const result = await redeemInviteCode(code);
      if (!result) {
        setError('Invite code not recognized. Check with your agent and try again.');
        return;
      }

      setSuccess(true);
    } catch (err) {
      const errCode = (err as { code?: string })?.code;
      if (errCode === 'functions/already-exists') {
        setError('This account is already linked to an insurance agent.');
      } else if (errCode === 'functions/permission-denied') {
        setError('Create an account to link with your agent.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header showBack title="Enter Invite Code" />
      <View style={[styles.content, { paddingBottom: insets.bottom + 80 }]}>
        {success ? (
          <View style={styles.successContainer}>
            <Text style={[styles.successTitle, { color: colors.text }]}>You're linked!</Text>
            <Text style={[styles.successMessage, { color: colors.textSecondary }]}>
              Your agent's insurance checklist will be pre-selected when you claim a property.
            </Text>
            <Button
              title="Done"
              onPress={() => router.back()}
              variant="primary"
              size="lg"
              fullWidth
            />
          </View>
        ) : (
          <>
            <Text style={[styles.description, { color: colors.textSecondary }]}>
              If your insurance agent gave you an invite code, enter it here to link your account to
              their insurance list.
            </Text>

            <Input
              label="Invite code"
              value={code}
              onChangeText={(v) => {
                setCode(v);
                setError(null);
              }}
              placeholder="e.g. ACME-JASON"
              autoCapitalize="characters"
              autoCorrect={false}
              returnKeyType="done"
              onSubmitEditing={handleSubmit}
            />

            <ErrorBanner message={error} />

            <Button
              title="Submit"
              onPress={handleSubmit}
              loading={isValidating}
              disabled={!code.trim() || isValidating}
              variant="primary"
              size="lg"
              fullWidth
            />
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: {
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
    gap: SPACING.md,
  },
  description: {
    ...TYPOGRAPHY.body,
  },
  successContainer: {
    gap: SPACING.md,
    paddingTop: SPACING.xl,
  },
  successTitle: {
    ...TYPOGRAPHY.statNumeral,
    textAlign: 'center',
  },
  successMessage: {
    ...TYPOGRAPHY.body,
    textAlign: 'center',
    marginBottom: SPACING.md,
  },
});
